<!--
  AI:
  Status: "in progress"
  Type: Component
  SubType: CrudIntegration
  Reason: [CW-SCHEMA] "Schema" navigation point — the read-only JSON of GET /cw/exchange/schema.
    Three states, all in German: laden (p-skeleton), Fehler (p-message, 403 = fehlende Rechte) and the
    result. Above the editor sits the one thing 4000 lines of JSON cannot show by itself: how many nodes
    and value lists the picture holds, and the gaps[] the schema itself declares it cannot answer.
    "Kopieren" hands over the whole document in one action — this view exists so that someone writes a
    skeleton from it, and select-all inside a several-thousand-line CodeMirror is where that fails.
    The editor is pinned read-only — see the component doc, an editable CodeMirror never adopts an
    HTTP-loaded document — and LogicLanguage.json always travels with showLanguageSelector = false.
-->
<div class="cw-case-type-schema p-2">

    <div class="flex align-items-center gap-2 flex-wrap mb-1">
        <i class="fa-regular fa-brackets-curly text-primary"></i>
        <span class="font-semibold">Schema</span>
        <p-tag value="Nur Lesen" severity="secondary"></p-tag>
        <span class="flex-auto"></span>
        <p-button label="Kopieren"
                  icon="fa-regular fa-copy"
                  severity="secondary"
                  size="small"
                  [outlined]="true"
                  [disabled]="!schemaJson"
                  pTooltip="Das vollständige Schema in die Zwischenablage kopieren"
                  tooltipPosition="top"
                  (onClick)="copyToClipboard()">
        </p-button>
        <p-button label="Aktualisieren"
                  icon="fa-regular fa-arrows-rotate"
                  severity="secondary"
                  size="small"
                  [outlined]="true"
                  [loading]="busy"
                  (onClick)="loadSchema()">
        </p-button>
    </div>

    <div class="text-sm text-color-secondary mb-3">
        <i class="fa-regular fa-circle-info mr-1"></i>
        Zeigt, welche Felder auf Basis der Konfiguration dieses Antragstyps zur Verfügung stehen —
        die Grundlage zum Schreiben des TAA-Skeletons (FreeMarker).
    </div>

    <!-- A missing right is an expected outcome of a point that stays visible to everyone, not a system
         failure — the same colour rule the preview panel applies to the identical 403. -->
    @if (error) {
        <p-message [severity]="errorSeverity" [text]="error" styleClass="w-full"></p-message>
    }

    @if (busy) {
        <div class="flex flex-column gap-2">
            <p-skeleton width="100%" height="2rem"></p-skeleton>
            <p-skeleton width="100%" height="20rem"></p-skeleton>
        </div>
    } @else if (schema) {

        <!-- Figures: the orientation the JSON itself cannot give -->
        <div class="flex align-items-center gap-3 flex-wrap mb-2 text-sm">
            @if (caseTypeLabel) {
                <span><span class="text-color-secondary">Antragstyp:</span>
                    <span class="font-medium ml-1">{{ caseTypeLabel }}</span></span>
            }
            <span><span class="text-color-secondary">Knoten:</span>
                <span class="font-medium ml-1">{{ nodeCount }}</span></span>
            <span><span class="text-color-secondary">Wertelisten:</span>
                <span class="font-medium ml-1">{{ valueListCount }}</span></span>
            @if (truncatedValueListCount > 0) {
                <p-tag [value]="truncatedValueListCount + ' Werteliste(n) gekürzt'" severity="warn"></p-tag>
            }
            @if (gaps.length > 0) {
                <p-tag [value]="gaps.length + ' Lücke(n)'" severity="warn"></p-tag>
            }
        </div>

        <!-- gaps[]: where the schema itself states what it cannot answer. Inside 4000 lines of JSON that
             would be invisible, so it is lifted ABOVE the editor. -->
        @if (gaps.length > 0) {
            <div class="cw-case-type-schema__gaps border-round-md p-2 mb-2">
                <div class="flex align-items-center gap-2 text-sm font-medium mb-1">
                    <i class="fa-regular fa-triangle-exclamation"></i>
                    <span>Was dieses Schema nicht beantwortet</span>
                </div>
                <ul class="m-0 pl-4 text-sm">
                    @for (gap of gaps; track gap + '#' + $index) {
                        <li>{{ gap }}</li>
                    }
                </ul>
            </div>
        }

        <div class="cw-case-type-schema__editor">
            <mvs-code-mirror-editor
                    [editorContent]="schemaJson"
                    [logicLanguage]="editorLanguage"
                    [showLanguageSelector]="false"
                    [readOnly]="true"
                    [fillHeight]="true">
            </mvs-code-mirror-editor>
        </div>

    } @else if (!error) {
        <div class="text-sm text-color-secondary">
            Für diesen Antragstyp liegt kein Schema vor. Prüfen Sie unter „Bipro", ob für diesen
            Antragstyp ein Austausch eingerichtet ist.
        </div>
    }
</div>
